import { useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronDown, BookOpen, Feather, X } from "lucide-react";

const questions = [
  {
    n: "I",
    q: "Where might I be following the traditions of men rather than the clear teaching of Christ and His apostles?",
    r: "Consider a belief or practice you have never questioned. Have you tested it against Scripture, or simply inherited it? Ask the Spirit to reveal what needs to be re-examined.",
  },
  {
    n: "II",
    q: "Am I reading Scripture to be informed, or am I opening my heart to be transformed by it?",
    r: "Transformation begins with a willing heart. What would change in your daily life if you truly let the Word reshape your thoughts, desires, and habits this week?",
  },
  {
    n: "III",
    q: "What conviction is the Word bringing that I have been reluctant to face?",
    r: "Conviction is a gift, not condemnation. Name the area where you sense correction, and receive it as an invitation to freedom rather than a burden of shame.",
  },
  {
    n: "IV",
    q: "How is God calling me toward repentance—not once, but as a continual turning toward Him?",
    r: "Repentance is a direction, not a single moment. What is one step you can take today to turn more fully toward the Messiah?",
  },
  {
    n: "V",
    q: "Where do I most need the life, hope, and restoration that Scripture promises to those who receive it in faith?",
    r: "Bring your weariness, doubt, or brokenness to the Word. Which promise of restoration do you most need to hold onto in this season?",
  },
  {
    n: "VI",
    q: "What would spiritual growth and a deeper relationship with the Messiah look like for me in the months ahead?",
    r: "Growth is measured in nearness, not achievement. Describe one way you long to know Christ more deeply, and what practice might nurture that closeness.",
  },
];

export const Reflect = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-reflect", handler);
    return () => window.removeEventListener("open-reflect", handler);
  }, []);

  const toggle = (i) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="reflect-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-2xl max-h-[88vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 border border-[#4A453F] bg-[#141311] p-7 md:p-10 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Close
            data-testid="reflect-close"
            className="absolute top-5 right-5 z-10 text-[#A39E98] hover:text-[#F4F0EA] transition-colors"
          >
            <X strokeWidth={1} className="h-6 w-6" />
          </DialogPrimitive.Close>

          <div className="flex items-center gap-2 mb-4">
            <BookOpen strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
              A Study in the Word
            </span>
          </div>
          <DialogPrimitive.Title className="font-heading font-light tracking-tight text-3xl md:text-5xl leading-tight text-[#F4F0EA] mb-4">
            Tested by the <span className="italic text-[#D4AF37]">Word of God</span>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-base leading-relaxed font-light text-[#A39E98] italic mb-6">
            Reflection questions to examine whether our faith and practice truly
            follow Christ.
          </DialogPrimitive.Description>

          <blockquote className="border-l-2 border-[#D4AF37] pl-5 text-sm md:text-base leading-relaxed font-light text-[#F4F0EA]/90 mb-8">
            The Word of God—the Holy Scriptures—is the ultimate standard by which
            every belief, doctrine, and way of life should be tested. Its purpose
            is not merely to inform, but to transform.
          </blockquote>

          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">
            Questions for Reflection
          </p>

          <div className="space-y-3">
            {questions.map((item, i) => {
              const isOpen = expanded.has(i);
              return (
                <button
                  key={item.n}
                  type="button"
                  data-testid={`reflect-q-${i}`}
                  aria-expanded={isOpen}
                  onClick={() => toggle(i)}
                  className="group w-full text-left border border-[#2B2825] bg-[#0A0A0A] px-5 py-5 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <span className="font-heading shrink-0 leading-none text-2xl text-[#D4AF37]/70">
                      {item.n}
                    </span>
                    <span className="flex-1">
                      <span className="block font-heading text-lg md:text-xl font-light leading-snug text-[#F4F0EA]">
                        {item.q}
                      </span>
                      <div
                        className="overflow-hidden transition-all duration-500"
                        style={{ maxHeight: isOpen ? "600px" : "0px", opacity: isOpen ? 1 : 0 }}
                      >
                        <span
                          data-testid={`reflect-reflection-${i}`}
                          className="block text-sm leading-relaxed font-light text-[#A39E98] pt-3"
                        >
                          {item.r}
                        </span>
                      </div>
                    </span>
                    <ChevronDown
                      strokeWidth={1.5}
                      className={`shrink-0 h-5 w-5 text-[#D4AF37] transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Feather strokeWidth={1.5} className="h-5 w-5 text-[#D4AF37] mx-auto mb-4" />
            <p className="font-heading italic text-xl md:text-2xl font-light text-[#D4AF37]">
              "Sanctify them by the truth; your word is truth."
            </p>
            <p className="mt-3 font-mono text-xs tracking-wide text-[#A39E98]">
              Take time. Pray. Let the Word have the final word.
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export const openReflect = () => {
  window.dispatchEvent(new Event("open-reflect"));
  if (typeof window.gtag === "function") window.gtag("event", "open_reflect", { category: "engagement" });
};
